import { css } from "@emotion/react";
import * as d3 from "d3";
import React from "react";
import useD3 from "./use-d3";
import { podtracPalette } from "./podtrac-palette";

/* ==================================================

WARNING: this currently makes some assumptions that
values are only positive and the bottom axis is at zero.

A "data" prop is expected that's an array of 
data with with an item per date for each series. A
"keys" array prop can be passed to fix the order of
the series or to filter out items from the data
that should not render.

[
    {
        date: "2022-07-21T00:00:00",
        series: "A",
        value: 7072,
    },
    {
        date: "2022-07-21T00:00:00",
        series: "B",
        value: 2441,
    },
    {
        date: "2022-07-22T00:00:00",
        series: "A",
        value: 20255,
    },
    {
        date: "2022-07-22T00:00:00",
        series: "B",
        value: 7042,
    },
    {
        date: "2022-07-23T00:00:00",
        series: "A",
        value: 7961,
    },
    {
        date: "2022-07-23T00:00:00",
        series: "B",
        value: 5327,
    },
]

The dates are expected to be serialized strings from
an API and called "date". If not, pass a "date"
accessor prop that will return a JS Date. (Parse UTC
dates as if they are local time. See note below.) A 
"period" prop (day|week|month) can be passed as hint
for formating x axis and tooltip dates.

The series name is expected to be called "series". If
not pass a custom "series" accessor prop.

It's likely that the series in the raw data will be
serialized ids, in which case you can pass a "labels"
prop that maps ids to display labels.

The value is expected to be called "value". If not, TODO TODO TODO TODO
pass a "value" accessor prop.

There is a default color scale but a "colorScale" prop
can be passed in if the scale is maintained outside
of the chart for other UI elements.

The chart tag will render any children inside its
outer div allowing for custom controls that float
above the svg.

TIMEZONE NOTE: this treats dates as local time to the
browser even though they are probably UTC dates from
the server. On ranges that are small enough, hours
will look shifted if dates are converted as UTC and
even though that is technically correct relative to
local time it's not actually the presentation that
we want.

====================================================*/

const tipDateFormat = d3.timeFormat("%B %d, %Y");
const tipMonthFormat = d3.timeFormat("%B %Y");

function ellipsis() {
    var self = d3.select(this),
        textLength = self.node().getComputedTextLength(),
        text = self.text(),
        width = 150,
        padding = 0;
    while (textLength > width - 2 * padding && text.length > 0) {
        text = text.slice(0, -1);
        self.text(text + "\u2026");
        textLength = self.node().getComputedTextLength();
    }
}

export default function StackedAreaChart({
    data = [],
    keys,
    labels = {},
    date = d => d.date,
    period,
    series = d => d.series,
    colorScale,
    value = d => d.value,
    metric,
    tickFormat = "~s", // SI units with insignificant trailing zeros trimmed
    tipFormat = ",.1~f", // see: https://github.com/d3/d3-format
    width = "100%",
    height = "15em",
    noborder,
    children
}) {
    const [elemRef, dimensionRef] = useD3(
        (svg, { width, height }) => {
            const margin = {
                top: 25,
                right: 15,
                bottom: 25,
                left: 45
            };

            const tipNumberFormat = d3.format(tipFormat);

            const w = width - margin.left - margin.right,
                h = height - margin.top - margin.bottom;

            const chartArea = svg.select(".chart-area");
            chartArea.attr("transform", `translate(${margin.left},${margin.top})`);

            if (!keys) {
                keys = [...d3.union(data.map(series))];
            }
            //console.log([...keys]);

            if (!colorScale) {
                colorScale = d3.scaleOrdinal(podtracPalette);
            }

            // TODO: when there's only one data point we need to expand the range
            // by one period on either end so the area draws something
            const indexed = d3.index(data, date, series);
            //console.table(indexed);

            var stackedData = d3
                .stack()
                .keys(keys)
                .value(([, group], key) => value(group.get(key)) ?? 0)(indexed);
            //console.log({ stackedData });

            var xScale = d3
                .scaleTime()
                .domain(d3.extent(indexed, ([key]) => key))
                .range([0, w]);

            const xAxis = d3.axisBottom(xScale);
            svg.select(".x-axis").attr("transform", `translate(${margin.left},${margin.top + h})`);

            var yScale = d3
                .scaleLinear()
                .domain([
                    0,
                    d3.max(indexed, ([_, group]) =>
                        d3.sum([...keys].map(key => value(group.get(key))))
                    )
                ])
                .range([h, 0])
                .nice(5); // mystery value... but it works.
            const yTicks = Math.ceil(h / 20);
            const yAxis = d3.axisLeft(yScale).ticks(yTicks, tickFormat);
            svg.select(".y-axis").attr("transform", `translate(${margin.left},${margin.top})`);

            const [x1, x2] = xScale.range();
            svg.select(".grid")
                .selectAll("line.grid-y")
                .data(yScale.ticks(yTicks))
                .join("line")
                .attr("class", "grid-y")
                .attr("x1", x1)
                .attr("y1", yScale)
                .attr("x2", x2)
                .attr("y2", yScale)
                .attr("stroke", "#eee");

            const [y1, y2] = yScale.range();
            svg.select(".grid")
                .selectAll("line.grid-x")
                //.data(xScale.ticks(/*timeDay*/))
                .data(xScale.ticks())
                .join("line")
                .attr("class", "grid-x")
                .attr("x1", xScale)
                .attr("y1", y1)
                .attr("x2", xScale)
                .attr("y2", y2)
                .attr("stroke", "#eee");

            const stackArea = d3
                .area()
                .curve(d3.curveStep)
                .x(d => xScale(d.data[0]))
                .y0(d => yScale(d[0]))
                .y1(d => yScale(d[1]));

            chartArea
                .select(".areas")
                .selectAll(".stacked")
                .data(stackedData, d => d.key)
                .join("path")
                .attr("class", d => "stacked")
                .attr("fill", d => colorScale(d.key))
                .attr("fill-opacity", 0.8)
                .attr("d", stackArea);

            const pointerX = svg
                .select(".pointerX")
                .attr("stroke", "#eee")
                .attr("stroke-dasharray", "4, 2")
                .attr("d", `M 0 0 V ${h}`)
                .style("display", "none");
            const pointerY = svg
                .select(".pointerY")
                .attr("stroke", "#eee")
                .attr("stroke-dasharray", "4, 2")
                .attr("d", `M 0 0 H ${w}`)
                .style("display", "none");

            const tip = {
                w: 235,
                row: 14,
                radius: 5,
                margin: {
                    top: 12,
                    right: 5,
                    bottom: 5,
                    left: 5
                }
            };
            tip.h = tip.margin.top + tip.margin.bottom + tip.row * keys.length;

            const toolTip = svg.select(".toolTip").style("display", "none");
            toolTip.select("rect").attr("width", tip.w).attr("height", tip.h);

            toolTip
                .selectAll(".date")
                .data([{}])
                .join("text")
                .attr("class", "date")
                .attr("x", tip.margin.left + tip.radius * 2 + tip.margin.left)
                .attr("y", tip.margin.top);

            toolTip
                .selectAll(".name")
                .data([labels[metric] ?? metric])
                .join("text")
                .attr("class", "name")
                .attr("x", tip.w - tip.margin.right)
                .attr("y", tip.margin.top)
                .attr("text-anchor", "end")
                .text(d => d);

            toolTip
                .selectAll(".color")
                .data(keys)
                .join("circle")
                .attr("class", "color")
                .attr("cx", tip.margin.left + tip.radius)
                .attr("cy", (d, i) => tip.margin.top + (i + 1) * tip.row - tip.radius + 2)
                .attr("r", tip.radius)
                .attr("fill", colorScale);

            toolTip
                .selectAll(".series")
                .data(keys)
                .join("text")
                .attr("class", "series")
                .attr("x", tip.margin.left + tip.radius * 2 + tip.margin.left)
                .attr("y", (key, i) => tip.margin.top + (i + 1) * tip.row)
                .text(key => {
                    const label = labels?.[key] ?? key;
                    return label;
                })
                .each(ellipsis);

            toolTip
                .selectAll(".value")
                .data(keys)
                .join("text")
                .attr("class", "value")
                .attr("x", tip.w - tip.margin.right)
                .attr("y", (key, i) => tip.margin.top + (i + 1) * tip.row)
                .attr("text-anchor", "end");

            svg.select(".x-axis").call(xAxis);
            svg.select(".y-axis").call(yAxis);

            const bisectDate = d3.bisector(d => d[0]).left;

            function mouseout() {
                pointerX.style("display", "none");
                pointerY.style("display", "none");
                toolTip.style("display", "none");
            }

            function mouseover() {}

            function mousemove(event) {
                const indexedArray = [...indexed];
                const p = d3.pointer(event),
                    x0 = xScale.invert(p[0]),
                    i = bisectDate(indexedArray, x0), // bisect takes array not iterable
                    d0 = indexedArray[i - 1],
                    d1 = indexedArray[i];

                if (d0 && d1) {
                    // because curveStep draws its area centered
                    // on the date axis we need to pick the date
                    // closest to the mouse instead of the date
                    // we are over
                    const d = x0 - d0[0] > d1[0] - x0 ? d1 : d0;

                    const xPos = p[0],
                        yPos = Math.min(p[1], h),
                        xTip = Math.max(0, Math.min(xPos - tip.w / 2, w - tip.w)),
                        yTip = Math.max(0, Math.min(yPos - tip.h, h - tip.h));

                    toolTip
                        .select(".date")
                        .datum(d)
                        .text(d => (period == "month" ? tipMonthFormat : tipDateFormat)(d[0]));

                    toolTip
                        .selectAll(".value")
                        .data(keys)
                        .text(key => tipNumberFormat(value(d[1].get(key)) ?? 0));

                    toolTip.attr("transform", `translate(${xTip}, ${yTip})`).style("display", null);

                    pointerX.attr("transform", `translate(${xPos}, 0)`).style("display", null);
                    pointerY.attr("transform", `translate(0, ${yPos})`).style("display", null);
                } else {
                    mouseout();
                }
            }

            const mouseRect = svg
                .select(".mouse")
                .attr("fill", "none")
                .style("pointer-events", "all")
                .attr("width", w)
                .attr("height", h + margin.bottom)
                .attr("transform", `translate(${margin.left},${margin.top})`)
                .on("mouseout", mouseout)
                .on("mouseover", mouseover)
                .on("mousemove", mousemove);
        },
        [data, keys, period, metric] // TODO: other deps?
    );
    return (
        <>
            <div
                ref={dimensionRef}
                style={{
                    width,
                    position: "relative"
                }}
            >
                <svg
                    ref={elemRef}
                    css={css`
                        flex: 1;
                        border: 1px solid ${noborder ? "transparent" : "#eee"};
                        display: block;
                        width: ${width};
                        height: ${height};

                        .axis {
                            color: #777;
                            shape-rendering: crispEdges;
                        }
                        .toolTip {
                            .date,
                            .name {
                                font-size: 10px;
                                font-weight: 700;
                            }
                            .value {
                                font-size: 10px;
                                font-variant-numeric: tabular-nums;
                            }
                            text {
                                font-size: 10px;
                            }
                            rect {
                                opacity: 0.9;
                                fill: white;
                                stroke: #eee;
                            }
                        }
                        .mouse {
                            cursor: crosshair;
                        }
                    `}
                >
                    <g className="chart-area">
                        <g className="grid" />
                        <g className="areas" />
                        <path className="pointerX" />
                        <path className="pointerY" />
                        <g className="toolTip" style={{ display: "none" }}>
                            <rect />
                        </g>
                    </g>
                    <g className="axis y-axis" />
                    <g className="axis x-axis" />
                    <rect className="mouse" />
                </svg>
                {children}
            </div>
        </>
    );
}
